import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAnalysisQueue } from '@/hooks/useAnalysisQueue';
import { Loader2, Play, Square, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

type PriorityMix = 'single' | 'mixed-enterprise-pro' | 'mixed-all';

interface JobStats {
  submitted: number;
  succeeded: number;
  rateLimited: number;
  failed: number;
  totalTime: number;
  priority1: { submitted: number; succeeded: number; rateLimited: number; avgTime: number };
  priority5: { submitted: number; succeeded: number; rateLimited: number; avgTime: number };
  priority10: { submitted: number; succeeded: number; rateLimited: number; avgTime: number };
}

export function LoadTester() {
  const [numJobs, setNumJobs] = useState(100);
  const [jobsPerSecond, setJobsPerSecond] = useState(2);
  const [priorityMix, setPriorityMix] = useState<PriorityMix>('mixed-enterprise-pro');
  const [singlePriority, setSinglePriority] = useState(5);
  const [isRunning, setIsRunning] = useState(false);
  const [shouldStop, setShouldStop] = useState(false);
  const [stats, setStats] = useState<JobStats>({
    submitted: 0,
    succeeded: 0,
    rateLimited: 0,
    failed: 0,
    totalTime: 0,
    priority1: { submitted: 0, succeeded: 0, rateLimited: 0, avgTime: 0 },
    priority5: { submitted: 0, succeeded: 0, rateLimited: 0, avgTime: 0 },
    priority10: { submitted: 0, succeeded: 0, rateLimited: 0, avgTime: 0 },
  });
  
  const { submitJob } = useAnalysisQueue();

  const getPriorityForJob = (index: number): number => {
    if (priorityMix === 'single') {
      return singlePriority;
    } else if (priorityMix === 'mixed-enterprise-pro') {
      // 50% Priority 1 (Enterprise), 50% Priority 5 (Pro)
      return index % 2 === 0 ? 1 : 5;
    } else {
      // mixed-all: 33% P1, 33% P5, 33% P10
      const mod = index % 3;
      return mod === 0 ? 1 : mod === 1 ? 5 : 10;
    }
  };

  const runLoadTest = async () => {
    setIsRunning(true);
    setShouldStop(false);
    setStats({
      submitted: 0,
      succeeded: 0,
      rateLimited: 0,
      failed: 0,
      totalTime: 0,
      priority1: { submitted: 0, succeeded: 0, rateLimited: 0, avgTime: 0 },
      priority5: { submitted: 0, succeeded: 0, rateLimited: 0, avgTime: 0 },
      priority10: { submitted: 0, succeeded: 0, rateLimited: 0, avgTime: 0 },
    });
    
    const startTime = Date.now();
    const interval = 1000 / jobsPerSecond;
    
    toast.info(`🚀 Starting stress test: ${numJobs} jobs at ${jobsPerSecond}/sec with ${priorityMix} priorities`);
    
    // Use a tiny 1x1 pixel valid PNG for load testing (will hit cache after first submission)
    const testImages = Array.from({ length: 24 }, (_, i) => 
      `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==`
    );
    
    const jobTimes: Record<number, number[]> = { 1: [], 5: [], 10: [] };
    
    for (let i = 0; i < numJobs && !shouldStop; i++) {
      const imageUrl = testImages[i % testImages.length];
      const priority = getPriorityForJob(i);
      const jobStartTime = Date.now();
      
      try {
        // Images are already in base64 format (data URIs)
        const jobId = await submitJob(imageUrl, {
          context: `Load test job ${i + 1}/${numJobs} [P${priority}]`,
          source: 'TEST',
          priority,
        });
        
        const jobTime = Date.now() - jobStartTime;
        
        if (jobId) {
          // Success - job submitted
          jobTimes[priority]?.push(jobTime);
          
          setStats(prev => {
            const priorityKey = `priority${priority}` as 'priority1' | 'priority5' | 'priority10';
            const priorityStats = prev[priorityKey];
            
            return {
              ...prev,
              submitted: prev.submitted + 1,
              succeeded: prev.succeeded + 1,
              [priorityKey]: {
                submitted: priorityStats.submitted + 1,
                succeeded: priorityStats.succeeded + 1,
                rateLimited: priorityStats.rateLimited,
                avgTime: jobTimes[priority].reduce((a, b) => a + b, 0) / jobTimes[priority].length,
              },
            };
          });
        } else {
          // Failed - job not submitted (null return from submitJob)
          throw new Error('Job submission returned null');
        }
      } catch (error: any) {
        const errorMessage = error?.message || String(error);
        const isRateLimited = errorMessage.includes('429') || 
                             errorMessage.includes('Rate limit') ||
                             errorMessage.includes('Too Many Requests') ||
                             errorMessage.includes('Queue is busy');
        
        console.error(`[Load Test] Job ${i + 1} failed (P${priority}):`, errorMessage);
        
        setStats(prev => {
          const priorityKey = `priority${priority}` as 'priority1' | 'priority5' | 'priority10';
          const priorityStats = prev[priorityKey];
          
          return {
            ...prev,
            submitted: prev.submitted + 1,
            rateLimited: isRateLimited ? prev.rateLimited + 1 : prev.rateLimited,
            failed: !isRateLimited ? prev.failed + 1 : prev.failed,
            [priorityKey]: {
              submitted: priorityStats.submitted + 1,
              succeeded: priorityStats.succeeded,
              rateLimited: isRateLimited ? priorityStats.rateLimited + 1 : priorityStats.rateLimited,
              avgTime: priorityStats.avgTime,
            },
          };
        });
        
        // Show warning on first rate limit detection
        if (isRateLimited && stats.rateLimited === 0) {
          toast.warning('⚠️ Rate limiting detected - this is expected behavior!');
        }
      }
      
      // Rate limiting between submissions
      if (i < numJobs - 1) {
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }
    
    const totalTime = Date.now() - startTime;
    setStats(prev => ({ ...prev, totalTime }));
    setIsRunning(false);
    
    const rateLimitPercent = stats.submitted > 0 && stats.rateLimited > 0 
      ? ((stats.rateLimited / stats.submitted) * 100).toFixed(1)
      : '0';
    
    toast.success(
      `✅ Stress test complete!\n` +
      `✓ ${stats.succeeded} succeeded, ${stats.rateLimited} rate limited (${rateLimitPercent}%), ${stats.failed} failed\n` +
      `⏱️ Duration: ${(totalTime/1000).toFixed(1)}s`
    );
  };

  const stopLoadTest = () => {
    setShouldStop(true);
    setIsRunning(false);
    toast.info('🛑 Stopping load test...');
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="numJobs">Total Jobs</Label>
          <Input
            id="numJobs"
            type="number"
            min="1"
            max="1000"
            value={numJobs}
            onChange={(e) => setNumJobs(parseInt(e.target.value) || 1)}
            disabled={isRunning}
          />
        </div>
        <div>
          <Label htmlFor="jobsPerSecond">Jobs per Second</Label>
          <Input
            id="jobsPerSecond"
            type="number"
            min="1"
            max="50"
            value={jobsPerSecond}
            onChange={(e) => setJobsPerSecond(parseInt(e.target.value) || 1)}
            disabled={isRunning}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="priorityMix">Priority Mix</Label>
          <Select 
            value={priorityMix} 
            onValueChange={(v) => setPriorityMix(v as PriorityMix)}
            disabled={isRunning}
          >
            <SelectTrigger id="priorityMix">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Single Priority</SelectItem>
              <SelectItem value="mixed-enterprise-pro">50% Enterprise (P1) + 50% Pro (P5)</SelectItem>
              <SelectItem value="mixed-all">33% P1 + 33% P5 + 33% P10</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {priorityMix === 'single' && (
          <div>
            <Label htmlFor="singlePriority">Priority Level</Label>
            <Select 
              value={String(singlePriority)} 
              onValueChange={(v) => setSinglePriority(parseInt(v))}
              disabled={isRunning}
            >
              <SelectTrigger id="singlePriority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 - Enterprise</SelectItem>
                <SelectItem value="5">5 - Pro/Standard</SelectItem>
                <SelectItem value="10">10 - Bulk</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Overall Stats */}
      <div className="space-y-2">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Queue Submission Results</div>
        <div className="grid grid-cols-4 gap-2 p-4 bg-muted rounded-lg">
          <div className="text-center">
            <div className="text-2xl font-bold">{stats.submitted}</div>
            <div className="text-xs text-muted-foreground">Submitted to Queue</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{stats.succeeded}</div>
            <div className="text-xs text-muted-foreground">Accepted by Queue</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{stats.rateLimited}</div>
            <div className="text-xs text-muted-foreground">Rate Limited</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
            <div className="text-xs text-muted-foreground">Submission Failed</div>
          </div>
        </div>
        <div className="text-xs text-muted-foreground italic px-2">
          ℹ️ These show submission to queue only. See "Performance Monitor" above for actual processing results.
        </div>
      </div>

      {/* Priority Breakdown */}
      {priorityMix !== 'single' && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Priority Breakdown</h4>
          <div className="grid grid-cols-3 gap-2">
            <div className="p-3 bg-purple-50 dark:bg-purple-950 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">Priority 1 (Enterprise)</div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span>Submitted:</span>
                  <span className="font-semibold">{stats.priority1.submitted}</span>
                </div>
                <div className="flex justify-between">
                  <span>Succeeded:</span>
                  <span className="font-semibold text-green-600">{stats.priority1.succeeded}</span>
                </div>
                <div className="flex justify-between">
                  <span>Rate Limited:</span>
                  <span className="font-semibold text-orange-600">{stats.priority1.rateLimited}</span>
                </div>
                <div className="flex justify-between">
                  <span>Avg Time:</span>
                  <span className="font-semibold">{stats.priority1.avgTime.toFixed(0)}ms</span>
                </div>
              </div>
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Priority 5 (Pro)</div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span>Submitted:</span>
                  <span className="font-semibold">{stats.priority5.submitted}</span>
                </div>
                <div className="flex justify-between">
                  <span>Succeeded:</span>
                  <span className="font-semibold text-green-600">{stats.priority5.succeeded}</span>
                </div>
                <div className="flex justify-between">
                  <span>Rate Limited:</span>
                  <span className="font-semibold text-orange-600">{stats.priority5.rateLimited}</span>
                </div>
                <div className="flex justify-between">
                  <span>Avg Time:</span>
                  <span className="font-semibold">{stats.priority5.avgTime.toFixed(0)}ms</span>
                </div>
              </div>
            </div>

            {priorityMix === 'mixed-all' && (
              <div className="p-3 bg-gray-50 dark:bg-gray-950 rounded-lg border border-gray-200 dark:border-gray-800">
                <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Priority 10 (Bulk)</div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span>Submitted:</span>
                    <span className="font-semibold">{stats.priority10.submitted}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Succeeded:</span>
                    <span className="font-semibold text-green-600">{stats.priority10.succeeded}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Rate Limited:</span>
                    <span className="font-semibold text-orange-600">{stats.priority10.rateLimited}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Avg Time:</span>
                    <span className="font-semibold">{stats.priority10.avgTime.toFixed(0)}ms</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test Results Summary */}
      {!isRunning && stats.submitted > 0 && (
        <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Test Results Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Total Duration:</span>
              <span className="font-semibold">{(stats.totalTime / 1000).toFixed(2)}s</span>
            </div>
            <div className="flex justify-between">
              <span>Success Rate:</span>
              <span className="font-semibold text-green-600">
                {((stats.succeeded / stats.submitted) * 100).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between">
              <span>Rate Limit Rate:</span>
              <span className="font-semibold text-orange-600">
                {((stats.rateLimited / stats.submitted) * 100).toFixed(1)}%
              </span>
            </div>
            {priorityMix !== 'single' && stats.priority1.avgTime > 0 && stats.priority5.avgTime > 0 && (
              <>
                <div className="pt-2 border-t">
                  <div className="flex justify-between">
                    <span>P1 Avg Submission Time:</span>
                    <span className="font-semibold">{stats.priority1.avgTime.toFixed(0)}ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span>P5 Avg Submission Time:</span>
                    <span className="font-semibold">{stats.priority5.avgTime.toFixed(0)}ms</span>
                  </div>
                  {stats.priority5.avgTime > stats.priority1.avgTime && (
                    <div className="mt-2 text-xs text-green-600 dark:text-green-400">
                      ✓ Priority queue working: P1 submitting faster than P5
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {!isRunning ? (
        <Button onClick={runLoadTest} className="w-full" size="lg">
          <Play className="w-4 h-4 mr-2" />
          Start Stress Test
        </Button>
      ) : (
        <Button onClick={stopLoadTest} variant="destructive" className="w-full" size="lg">
          <Square className="w-4 h-4 mr-2" />
          Stop Test
        </Button>
      )}

      {isRunning && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Running stress test... ({stats.submitted}/{numJobs})
        </div>
      )}
    </div>
  );
}
